import * as React from 'react'
import {Box, Typography, ButtonBase} from '@mui/material'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import {alertStripSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, MUTED, BORDER, WHITE, WARNING, SUCCESS, ERROR, withOpacity} from '../../../styles/foundation/colors'
import {fmtKg, fmtKgOrDash, dimsText, dimsSpoken} from './format'
import {DISPUTE_TYPE} from './data/reference'
import MUIModal from '../../../common/MUIModal'
import {evidenceScore} from './state/evidenceScore'
import {courierScanSrcs, merchantEvidenceMedia} from './data/scanImage'
import {governingType} from './state/query'

/**
 * Weight-comparison and evidence markup, shared between DisputeDrawer.js and
 * BillingRecord.js. Per the mockup's own note on `PZ.proof`: these two
 * sections must be pure `(record) -> markup` functions consumed by BOTH
 * surfaces, specifically so a merchant can never see the drawer and the
 * courier billing record disagree about the same AWB's weight math or
 * evidence status. Do not copy this markup into either consumer — import it.
 */

const EVIDENCE_COLOR = {none: ERROR, weak: WARNING, strong: SUCCESS}

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const rowSx = {display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.5, gap: 2}
// SIZE.xs, the section-heading size the weight drawers share. It has to live
// here rather than at the call site: DisputeDrawer prints two of these headings
// itself and the two below render inside the same drawer, so a call-site
// override would leave one drawer heading two different sizes. BillingRecord
// moves with it by design — it exists to show these same sections.
export const sectionHeadingSx = {fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 1}

// Bold-navy for whichever of dead/volumetric governs this row's applied
// weight, subtle for the other — the exact same governSx/subtleTextSx
// contrast DisputeGrid.js uses for its Billed column sub-line, so a
// merchant can never see the grid bold one figure and the drawer bold the
// other for the same AWB.
const governSx = {fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY}
const notGovernSx = {fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}

/**
 * The Billed card spoken aloud. Exported so the governing-weight signal stays
 * testable: on screen it is font weight alone, which Testing Library cannot
 * check without the node access this repo's lint rules forbid.
 */
export function billedCardSummary(row, governing) {
  const label = DISPUTE_TYPE[governing] ? DISPUTE_TYPE[governing].label : governing
  return [
    `Billed ${fmtKg(row.appliedWeight)}`,
    `dead weight ${fmtKgOrDash(row.deadKg)}`,
    `volumetric ${fmtKgOrDash(row.volKg)}`,
    dimsSpoken(row.dims),
    `governed by ${label}`,
  ].join(', ')
}

/**
 * One side of the comparison, as a card: the chargeable figure, then the two
 * measurements behind it and the carton they came from.
 *
 * `governs` bolds whichever of dead/volumetric produced the headline. It is
 * only ever passed for the billed side — on the declared side the merchant
 * states one weight and, by construction, their volumetric always lands below
 * it, so dead would "win" every row and the emphasis would say nothing.
 */
function WeightCard({label, caption, figure, dead, vol, dims, governs, accent, tinted, srSummary}) {
  const deadGoverns = governs === 'HIGHER_DEAD' || governs === 'HIGHER_BOTH'
  const volGoverns = governs === 'HIGHER_VOL' || governs === 'HIGHER_BOTH'

  return (
    <Box sx={{
      border: `1px solid ${BORDER}`,
      borderLeft: `3px solid ${accent}`,
      borderRadius: '10px',
      bgcolor: tinted ? withOpacity(accent, 0.04) : WHITE,
      p: 1.25,
      minWidth: 0,
    }} aria-label={srSummary}>
      <Typography sx={{fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
        {label}
      </Typography>
      <Typography sx={{fontFamily: FONT, fontSize: '18px', fontWeight: WEIGHT.bold, color: NAVY, lineHeight: 1.2, mt: 0.25}}>
        {figure}
      </Typography>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: MUTED}}>{caption}</Typography>

      <Box sx={{borderTop: `1px solid ${BORDER}`, mt: 0.75, pt: 0.75, display: 'grid'}}>
        <Box sx={{...rowSx, py: 0.25}}>
          <Typography sx={labelSx}>Dead</Typography>
          <Typography sx={deadGoverns ? governSx : notGovernSx}>{dead}</Typography>
        </Box>
        <Box sx={{...rowSx, py: 0.25}}>
          <Typography sx={labelSx}>Volumetric</Typography>
          <Typography sx={volGoverns ? governSx : notGovernSx}>{vol}</Typography>
        </Box>
        {dims && (
          <Typography sx={{fontFamily: FONT, fontSize: '11px', color: MUTED, fontVariantNumeric: 'tabular-nums', mt: 0.25}}>
            {dims}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

/**
 * Declared against billed, as two cards side by side rather than one column of
 * label/value rows. The drawer's job at this point is a single comparison —
 * what you said the parcel weighed versus what the courier charged for — and a
 * flat list made the reader hold six numbers in their head to make it. Side by
 * side, each figure sits directly opposite its counterpart.
 *
 * Two cards, not three. The reference layout separates "courier measured"
 * from "charged" because a slab is applied between them; this dataset applies
 * no slab to weights — appliedWeight IS max(deadKg, volKg) by construction, and
 * only the money uses the 0.5 kg slab convention (see format.js's EXCESS_NOTE).
 * A third card would have printed the middle one's numbers twice.
 *
 * Which of dead/volumetric governs comes from governingType(row) — the same
 * helper DisputeGrid's Billed column uses, never a local re-derivation — so the
 * grid and the drawer can never bold different figures for the same row.
 */
export function WeightComparisonSection({row}) {
  // Null rather than zeros: a card with no carton says so, instead of showing "0 × 0 × 0 cm".
  // The drawer's cards are roomier than a grid cell, so they keep the spaced triple they had.
  const dims = dimsText(row.dims, ' × ')
  const dDims = dimsText(row.declaredDims, ' × ')
  const gType = governingType(row)

  return (
    <>
      <Typography sx={sectionHeadingSx}>Weight comparison</Typography>

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 1.5}}>
        <WeightCard
          label='Declared'
          figure={fmtKg(row.originalWeight)}
          caption='what you declared'
          // The dead weight the merchant put on the scale once they have submitted one — the dims
          // below already switch to their submitted carton, and showing the booking figure beside it
          // made the reading they had just entered look lost.
          dead={row.claimedDeadKg != null ? `${fmtKg(row.claimedDeadKg)} (scale reading)` : fmtKg(row.originalWeight)}
          vol={fmtKg(row.declaredVolKg)}
          dims={dDims}
          accent={BORDER}
        />
        <WeightCard
          label='Billed'
          figure={fmtKg(row.appliedWeight)}
          caption='what the courier charged'
          dead={fmtKgOrDash(row.deadKg)}
          vol={fmtKgOrDash(row.volKg)}
          dims={dims}
          governs={gType}
          accent={WARNING}
          tinted
          // On screen, which of dead/volumetric governs is carried only by
          // bold-vs-muted weight. That is invisible to a screen reader, and the
          // "Higher Dead Weight" caption that used to say it outright is gone —
          // so without this, removing that line would have been a real a11y
          // regression rather than a tidy-up. Same fix, and the same wording,
          // as DisputeGrid's billedSummary().
          srSummary={billedCardSummary(row, gType)}
        />
      </Box>

      {/* The working line, the Excess weight row, the dispute-type label and
          the excess-freight strip have all gone. Each repeated something the
          reader already had: the working restates arithmetic on the dims
          printed directly above it, Excess weight and Excess freight are their
          own columns in the grid this drawer opened from, and the type label
          ("Higher Dead Weight") says in words exactly what the bolded Dead row
          in the Billed card says in weight. Four restatements were costing more
          vertical space than the comparison itself. */}
    </>
  )
}

/**
 * Evidence heading, the win-rate alert strip, courier scan thumbnails and the
 * merchant's own attached evidence.
 *
 * `hideCourierScans` drops the courier thumbnail strip, for hosts that already
 * render the scan themselves.
 *
 * `onImageClick(src)` fires when a courier scan thumbnail is clicked — the
 * caller owns the lightbox state (see DisputeDrawer.js and BillingRecord.js,
 * which each render their own <Lightbox/> alongside this section). Omit it
 * to render the thumbnails as non-interactive.
 */
export function EvidenceSection({row, onImageClick, hideCourierScans}) {
  const evidence = evidenceScore(row)
  // The real uploads on a live row, the generated stand-ins on a seeded one — see
  // data/scanImage.js#courierScanSrcs. One list for the merchant's side so the strip renders in a
  // single pass and the "nothing uploaded" case is a single emptiness check.
  const courierThumbs = courierScanSrcs(row)
  const evidenceThumbs = merchantEvidenceMedia(row)
  // The packing video plays here, in the page. Opened as a link it downloaded instead: the stored
  // file carries no video content type, which a <video> element tolerates and a browser tab does not.
  const [playing, setPlaying] = React.useState(null)

  return (
    <>
      <Typography sx={sectionHeadingSx}>Evidence</Typography>
      <Box sx={{...alertStripSx(EVIDENCE_COLOR[evidence.level]), mb: 1.5}}>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold}}>
          {evidence.label}
        </Typography>
      </Box>

      {/* Suppressed when the host already shows the scan at full size — see
          DisputeDrawer.js. Repeating the same photo as a thumbnail directly
          beneath it is noise, not a second piece of evidence. */}
      {!hideCourierScans && (
      <Typography sx={{...labelSx, mb: 0.5}}>Courier scan images ({courierThumbs.length})</Typography>
      )}
      {hideCourierScans ? null : courierThumbs.length === 0 ? (
        <Typography sx={{...labelSx, mb: 1.5}}>No courier scan images on file.</Typography>
      ) : (
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5}}>
          {courierThumbs.map((src, i) => (
            <ButtonBase
              key={i}
              onClick={() => onImageClick && onImageClick(src)}
              aria-label={`View courier scan image ${i + 1} of ${courierThumbs.length} for AWB ${row.awb}`}
              sx={{width: 96, height: 68, borderRadius: '8px', overflow: 'hidden', border: `1px solid ${BORDER}`}}
            >
              <Box component='img' src={src} alt='' sx={{width: '100%', height: '100%', objectFit: 'cover'}} />
            </ButtonBase>
          ))}
        </Box>
      )}

      {/* The proof itself, not a chip asserting it exists. A merchant looking
          at a dispute needs to see WHICH shots were sent — a count cannot tell
          them whether the scale photo is in there. The video keeps a chip
          because there is no frame to show for it. */}
      <Typography sx={{...labelSx, mb: 0.5}}>Your evidence</Typography>
      {/* Video and photos share one strip, and the video is a thumbnail like
          the rest rather than a chip: it is the strongest evidence a merchant
          can send, so it should not be the one item rendered as a label. Its
          own frame carries a play badge, which is what distinguishes it. */}
      {evidenceThumbs.length > 0 && (
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1}}>
          {evidenceThumbs.map((t, i) => (
            <ButtonBase
              key={t.key}
              // A live video plays in the player below; the image viewer shows images only.
              onClick={() => (t.href ? setPlaying(t.href) : onImageClick && onImageClick(t.src))}
              aria-label={`View ${t.name}, item ${i + 1} of ${evidenceThumbs.length} for AWB ${row.awb}`}
              sx={{
                position: 'relative', width: 96, height: 68, borderRadius: '8px',
                overflow: 'hidden', border: `1px solid ${BORDER}`,
              }}
            >
              {t.href ? (
                <Box component='video' src={t.src} preload='metadata' muted playsInline sx={{width: '100%', height: '100%', objectFit: 'cover', bgcolor: NAVY}} />
              ) : (
                <Box component='img' src={t.src} alt='' sx={{width: '100%', height: '100%', objectFit: 'cover'}} />
              )}
              {t.isVideo && (
                <VideocamOutlinedIcon
                  aria-hidden='true'
                  sx={{
                    position: 'absolute', bottom: 4, right: 4, fontSize: 16, color: WHITE,
                    bgcolor: withOpacity(NAVY, 0.6), borderRadius: '4px', p: '1px',
                  }}
                />
              )}
            </ButtonBase>
          ))}
        </Box>
      )}
      {evidenceThumbs.length === 0 && (
        <Typography sx={{...labelSx, mb: 1}}>No evidence uploaded yet.</Typography>
      )}
      <MUIModal open={!!playing} handleClose={() => setPlaying(null)} title={`Packing video · AWB ${row.awb}`} maxWidth='md'>
        {playing && (
          <Box component='video' src={playing} controls autoPlay playsInline
            sx={{display: 'block', width: '100%', maxHeight: '70vh', bgcolor: NAVY, borderRadius: '8px'}} />
        )}
      </MUIModal>
    </>
  )
}
